import { useLocalization } from "@progress/kendo-react-intl";
import { useEffect, useRef, useState } from "react";
import { AccessToken } from "../config/AccessToken";
import axios from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import {
  TransformWrapper,
  TransformComponent,
  useControls
} from "react-zoom-pan-pinch";
import { Checkbox, Switch } from "@progress/kendo-react-inputs";

import { AzureMapComponent } from "./AzureMapComponent";
import { useNavigate } from "react-router-dom";
import { Avatar } from "@progress/kendo-react-layout";
import { SvgIcon } from "@progress/kendo-react-common";
import { trashIcon } from "@progress/kendo-svg-icons";
import { NotificationGroup, Notification } from "@progress/kendo-react-notification";
import { Fade } from "@progress/kendo-react-animation";
import { currentLocation } from "../assets/current-location";
import { filterBy } from "@progress/kendo-data-query";
import { loadingPanel } from "../components/LoadingPanel";


/**
 * Component that displays and manages a map interface with various functionalities.
 * It allows users to interact with maps, zones, groups, beacons, and alarms.
 * Handles data fetching from server and local storage, and renders map layers and markers.
 * Provides options to toggle live updates, and to manage selections using checkboxes and dropdowns.
 * 
 * @param {Object} props - The properties passed to the component.
 */
export const Maps = (props: any) => {

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  const localizationService = useLocalization();
  const [instance, accessTokenRequest] = AccessToken();

  const [selectedMap, setSelectedMap] = useState<any>([]);

  const [listMaps, setListMaps] = useState([]);
  const [mapName, setMapName] = useState([]);
  const [listMapNames, setListMapNames] = useState([]);

  const [zonesCheckboxByMap, setZonesCheckboxByMap] = useState<any>([]);
  const [zoneCheckedList, setZoneCheckedList] = useState<any>([]);

  const [groupsCheckboxByMap, setGroupsCheckboxByMap] = useState<any>([]);
  const [groupChanged, setGroupChanged] = useState<any>(false);

  const [deviceChecked, setDeviceChecked] = useState<any>([]);

  const [allBeacons, setAllBeacons] = useState([]);
  const [listBeaconsOn, setListBeaconsOn] = useState<any>([]);

  const [currentZoneCanvas, setCurrentZoneCanvas] = useState<any>([]);

  const [mapGeoreferenced, setMapGeoreferenced] = useState(false);

  const [loading, setLoading] = useState(false);
  const isChangingMapRef = useRef(false);
  const hasRefreshedForSynoptic = useRef(false);

  const navigate = useNavigate()

  let mapCanvas: any = useRef()
  let mapCtx = mapCanvas?.current?.getContext("2d");

  const zoneCanvas: any = useRef();
  const groupCanvas: any = useRef();
  const alarmCanvas: any = useRef();
  
  // SVG marker state for better quality
  const [groupMarkersSVG, setGroupMarkersSVG] = useState<any>([]);
  const [beaconMarkersSVG, setBeaconMarkersSVG] = useState<any>([]);

  const deviceMacFromSynoptic: any = localStorage.getItem('deviceMacFromSynoptic')
  const deviceMapIdFromSynoptic: any = localStorage.getItem('deviceMapIdFromSynoptic')
  const beaconId: any = localStorage.getItem('beaconId')
  const mapIdByBeacon: any = localStorage.getItem('mapIdByBeacon')
  const alarmInfoSelected: any = JSON.parse(localStorage.getItem('alarmInfoSelected') as string)

  const [alarmChecked, setAlarmChecked] = useState<any>(alarmInfoSelected);
  const [alarmToggle, setAlarmToggle] = useState<any>(false);
  const [alarmInfo, setAlarmInfo] = useState<any>([]);

  const [liveStatusMap, setLiveStatus] = useState<any>(false);
  const [mapId, setMapId] = useState<any>(null);

  const [showDeviceMacBySynoptic, setShowDeviceMacBySynoptic] = useState<any>(false);
  const [deviceMacSelected, setDeviceMacSelected] = useState<any>(deviceMacFromSynoptic);
  const [noPositionFound, setNoPositionFound] = useState<any>(false);

  const [showBeaconMac, setShowBeaconMac] = useState<any>(false);
  const [beaconIdSelected, setBeaconIdSelected] = useState<any>(beaconId);
  const [beaconsFromGrid, setBeaconsFromGrid] = useState<any>([]);
  const [selectedBeacons, setSelectedBeacons] = useState<any>([]);

  const [onlyBound, setOnlyBound] = useState<any>(false);
  const [toggleGeoStatic, setToggleGeoStatic] = useState<any>(true);
  const [groupingEnabled, setGroupingEnabled] = useState<boolean>(() => {
    const storedGrouping = localStorage.getItem('groupingEnabledMap');
    if (storedGrouping == null) {
      return true;
    }
    return storedGrouping === 'true';
  });
  const [noMaps, setNoMaps] = useState<any>(false);

  //manager tooltip groups
  const [transformState, setTransformState] = useState<any>([])
  const [tooltipContent, setTooltipContent] = useState("");
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [lastMouseEvent, setLastMouseEvent] = useState(null);
  const transformContentRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  let languageId = localStorage.getItem('languageId');

  useEffect(() => {
    getMapList()
  }, []);

  useEffect(() => {
    const mapId: any = localStorage.getItem('mapId')
    setMapId(mapId)
  }, [localStorage.getItem('mapId')]);

  useEffect(() => {
    if (zoneCanvas.current != null) {
      setCurrentZoneCanvas(zoneCanvas);
    }
  }, [mapGeoreferenced]);

  // reset localstorage on page load 
  useEffect(() => {
    localStorage.setItem('zonesChecked', '');
    localStorage.setItem('devicesChecked', '');
    localStorage.removeItem('onlyBoundMap');
    setShowDeviceMacBySynoptic(false);
    setShowBeaconMac(false);
    setSelectedBeacons([]);
  }, [])

  // Ensure synoptic deep link refreshes positions immediately on load
  useEffect(() => {
    if (!deviceMacFromSynoptic) {
      return;
    }

    if (hasRefreshedForSynoptic.current) {
      return;
    }

    const storedMapId = mapId ?? localStorage.getItem('mapId');
    if (!storedMapId || storedMapId === '' || storedMapId === 'null' || storedMapId === 'undefined') {
      return;
    }

    let parsedMapId: number | null = null;

    if (typeof storedMapId === 'number') {
      parsedMapId = storedMapId;
    } else {
      try {
        parsedMapId = JSON.parse(storedMapId);
      } catch {
        const fallbackNumber = Number(storedMapId);
        parsedMapId = Number.isFinite(fallbackNumber) ? fallbackNumber : null;
      }
    }

    if (parsedMapId === null) {
      return;
    }

    refreshGroupPositionsInMap(parsedMapId, mapGeoreferenced);
    hasRefreshedForSynoptic.current = true;
  }, [deviceMacFromSynoptic, mapGeoreferenced, mapId]);

  const getMapList = async () => {

    setLoading(true)

    localStorage.setItem('mapId', '');

    const commonOperations = async (accessToken: any) => {
      try {
        const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
        const res = await axios.get(`${SERVER_URL}/map/full/list`, { headers });
        const data = res?.data.data;

        if (!data?.length) {
          setNoMaps(true);
          setLoading(false);
          return;
        }

        const mapsList = data;
        const mapNames = data.map((map: any) => map.name);
        const mainMap = data.find((map: any) => map.main == '1');

        setListMaps(mapsList);
        setListMapNames(mapNames);

        getGroupList();

        if (deviceMacFromSynoptic != null) {
          if (!deviceMapIdFromSynoptic) {
            setNoPositionFound(true);
            setTimeout(() => setNoPositionFound(false), 5000);
            return;
          }

          const mapSelected = mapsList.find((map: any) => map.id === Number(deviceMapIdFromSynoptic)) ?? mapsList[0];
          setSelectedMap(mapSelected);
          localStorage.setItem('mapId', JSON.stringify(mapSelected.id));
          localStorage.removeItem('mapGeoreferenced');
          await changeMap(mapSelected, false, mapsList);
          setShowDeviceMacBySynoptic(true);
          return;
        }

        else if (mapIdByBeacon != null) {
          const mapSelected = mapsList.find((map: any) => map.id === Number(mapIdByBeacon)) ?? mapsList[0];
          if (mapSelected) {
            localStorage.removeItem('mapGeoreferenced');
            await changeMap(mapSelected, false, mapsList);
            setSelectedMap(mapSelected);
            localStorage.setItem('mapId', JSON.stringify(mapIdByBeacon));
            setShowBeaconMac(true);
            return;
          }
        }

        else if (alarmInfoSelected != null) {
          localStorage.removeItem('mapGeoreferenced');
          localStorage.setItem("alarmInfoSelected", JSON.stringify(alarmInfoSelected));
          //setAlarmToggle(true)
          setAlarmChecked(alarmInfoSelected);
          setAlarmInfo(alarmInfoSelected);
          getAlarmPositionByWakeupId(alarmInfoSelected.info.id, mapsList);
          alarmChecked.checked = true;
          return;
        }
        else {
          const currMapSelected = (() => {
            try {
              const parsed = selectedMap || 'null';
              return parsed;
            } catch {
              return null;
            }
          })();

          const fallbackMap =
            (Array.isArray(currMapSelected) && currMapSelected.length > 0 && currMapSelected) ||
            (!Array.isArray(currMapSelected) && typeof currMapSelected === 'object' && currMapSelected) ||
            mainMap ||
            mapsList[0];

          const fallbackMapId = fallbackMap?.id ?? '-1';

          localStorage.setItem('mapId', JSON.stringify(fallbackMapId));
          setSelectedMap(fallbackMap);
          await changeMap(fallbackMap, false, mapsList);
        }

      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect(accessTokenRequest);
        }
      } 
      //finally {
      //  setLoading(false);
      //}
    };


    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        });
    }
  }

  // refresh groups positions in map
  useEffect(() => {
    const refreshAPI: any = localStorage.getItem('refreshAPI')
    const interval = setInterval(async () => {
      // Skip refresh if map is being changed
      if (isChangingMapRef.current) return;
      
      let mapId = JSON.parse(localStorage.getItem('mapId') || '');

      if (localStorage.getItem('mapGeoreferenced') == 'false' && mapId != '-1' && mapId != '') {
        refreshGroupPositionsInMap(mapId, false)
      }
      if (localStorage.getItem('mapGeoreferenced') == 'true') {
        refreshGroupPositionsInMap(mapId, true)
      }
    }, refreshAPI * 1000);

    return () => clearInterval(interval);
  }, [transformState])

  const Controls = () => {
    const { zoomIn, zoomOut, resetTransform } = useControls();

     return (
            <div style={{ display: 'flex', zIndex: 0 }}>
                <button
                    className="k-button static-map"
                    title={localizationService.toLanguageString('map.zoom_in', 'NOT_FOUND')}
                    onClick={() => zoomIn()}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M12 5v14" />
                        <path d="M5 12h14" />
                    </svg>
                </button>
                <button
                    className="k-button static-map"
                    title={localizationService.toLanguageString('map.zoom_out', 'NOT_FOUND')}
                    onClick={() => zoomOut()}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M5 12h14" />
                    </svg>
                </button>
                <button
                    className="k-button static-map"
                    title={localizationService.toLanguageString('map.reset_zoom', 'NOT_FOUND')}
                    onClick={() => resetTransform()}
                >
                    <SvgIcon icon={currentLocation} />
                </button>
            </div>
        );
  };

  const stringToBoolean = (str: any) => {
    return str?.toLowerCase() === 'true';
  }

  const [mapImageWidth, setMapImageWidth] = useState<any>(0);
  const [mapImageHeight, setMapImageHeight] = useState<any>(0);
  const [mapImageData, setMapImageData] = useState<string | null>(null);

  const getLatLong = (position: any) => {
    const longitude = Number(position?.longitude);
    const latitude = Number(position?.latitude);

    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
      return null;
    }

    return { longitude, latitude };
  };

  const projectPointOnCanvas = (position: any, width: number, height: number) => {
    if (width <= 0 || height <= 0) {
      return null;
    }

    const coords = getLatLong(position);
    if (!coords) {
      return null;
    }

    return {
      x: width * coords.longitude,
      y: height - height * coords.latitude
    };
  };

  // Ridisegna i marker SVG quando cambia lo zoom o le dimensioni della mappa
  useEffect(() => {
    if (!mapGeoreferenced && transformState && mapImageWidth > 0 && mapImageHeight > 0) {
      const devicesCheckedStr = localStorage.getItem('devicesChecked');
      
      if (devicesCheckedStr && devicesCheckedStr !== '' && devicesCheckedStr !== '[]') {
        const devicesChecked = JSON.parse(devicesCheckedStr);
        
        if (devicesChecked.length > 0) {
          const scale = transformState?.scale ?? 1;
          const newSVGMarkers: any = [];
          
          // Ridisegna i marker solo per i dispositivi già filtrati
          devicesChecked.forEach((device: any) => {
            const projectedPoint = projectPointOnCanvas(device?.position, mapImageWidth, mapImageHeight);
            if (!projectedPoint) {
              return;
            }

            const x = Math.round(projectedPoint.x) + 4.5;
            const y = Math.round(projectedPoint.y) - 4;
            
            // Dimensioni fisse del marker
            const baseMarkerRadius = 16;
            const baseHoleRadius = 12;
            const offset = 4;
            
            const angleOffset = Math.PI / 6;
            const leftX = x - offset - Math.sin(angleOffset) * baseMarkerRadius;
            const leftY = y - offset - baseMarkerRadius + Math.cos(angleOffset) * baseMarkerRadius;
            const rightX = x - offset + Math.sin(angleOffset) * baseMarkerRadius;
            const rightY = y - offset - baseMarkerRadius + Math.cos(angleOffset) * baseMarkerRadius;
            
            newSVGMarkers.push({
              x: x - offset,
              y: y - offset - baseMarkerRadius,
              markerRadius: baseMarkerRadius,
              holeRadius: baseHoleRadius,
              leftX,
              leftY,
              rightX,
              rightY,
              tipX: x - offset,
              tipY: y,
              color: device?.color ?? device?.groupColor ?? 'lightgrey',
              deviceMac: device?.mac ?? device?.deviceMac,
              deviceName: device?.name,
              scale: 1 / scale
            });
          });
          
          setGroupMarkersSVG(newSVGMarkers);
        } else {
          // Se non ci sono dispositivi checked, svuota i marker
          setGroupMarkersSVG([]);
        }
      } else {
        // Se devicesChecked è vuoto, svuota i marker
        setGroupMarkersSVG([]);
      }
      
      // Ridisegna i beacon marker quando cambia lo zoom
      const beaconsCheckedStr = localStorage.getItem('beaconsByMapId');
      
      if (beaconsCheckedStr && beaconsCheckedStr !== '' && beaconsCheckedStr !== '[]') {
        const beaconsList = JSON.parse(beaconsCheckedStr);
        const scale = transformState?.scale ?? 1;
        const newBeaconMarkers: any = [];
        
        beaconsList.forEach((beacon: any) => {
          if (!beacon.checked) {
            return;
          }

          const projectedPoint = projectPointOnCanvas(beacon, mapImageWidth, mapImageHeight);
          if (!projectedPoint) {
            return;
          }

          const x = projectedPoint.x;
          const y = projectedPoint.y;
            
          newBeaconMarkers.push({
            x: x,
            y: y,
            color: beacon.color,
            name: beacon.name,
            type: beacon.type,
            id: beacon.id,
            scale: 1 / scale
          });
        });
        
        setBeaconMarkersSVG(newBeaconMarkers);
      } else {
        setBeaconMarkersSVG([]);
      }
    }
  }, [transformState, mapImageWidth, mapImageHeight, mapGeoreferenced, deviceChecked]);

  const renderMap = (map: any) => {

    const isMapGeo: any = stringToBoolean(localStorage.getItem('mapGeoreferenced'))

    if ((map?.georeferenced == 0 || !isMapGeo)) {

      mapCtx?.clearRect(0, 0, mapCtx?.canvas.width, mapCtx?.canvas.height)

      let backgroundCanvas = new Image();

      setMapImageData(map?.image ?? null);
      backgroundCanvas.src = `data:image/jpeg;base64, ${map?.image ?? ''}`;

      backgroundCanvas.onload = () => {
        const wImg = backgroundCanvas.width;
        const hImg = backgroundCanvas.height;

        setMapImageWidth(wImg);
        setMapImageHeight(hImg);

      };

      setMapGeoreferenced(false)
    }
    else {
      setMapImageData(null);
      setMapGeoreferenced(true)
    }
  }

  useEffect(() => {
    if (!mapCanvas.current || mapImageWidth === 0 || !mapImageData) return;

    // imposta dimensioni canvases (groupCanvas e beaconCanvas ora usano SVG)
    mapCanvas.current.width =
      zoneCanvas.current.width =
      alarmCanvas.current.width = mapImageWidth;

    mapCanvas.current.height =
      zoneCanvas.current.height =
      alarmCanvas.current.height = mapImageHeight;

    // disegno la mappa
    const ctx = mapCanvas.current.getContext("2d");
    const img = new Image();
    img.src = "data:image/jpeg;base64," + mapImageData;

    img.onload = () => {
      ctx.clearRect(0, 0, mapImageWidth, mapImageHeight);
      ctx.drawImage(img, 0, 0, mapImageWidth, mapImageHeight);
    };

  }, [mapImageWidth, mapImageHeight, mapGeoreferenced, mapImageData]);


  const [initialScale, setInitialScale] = useState(0.7);

  const changeMap = async (event: any, fromMap?: boolean, mapsList?: any) => {

    isChangingMapRef.current = true;
    setLoading(true);
    setGroupMarkersSVG([]);
    setNoPositionFound(false);
    
    // Reset scale when changing map
    setInitialScale(0.7)

    let currMap: any = ''
    if (event?.value != undefined) {
      currMap = event?.value ?? event ?? ''
    } else {
      currMap = event.name ?? event ?? ''
    }

    setZoneCheckedList([])
    setZonesCheckboxByMap([])
    setGroupsCheckboxByMap([])
    setListBeaconsOn([])
    setSelectedBeacons([])

    localStorage.setItem('liveStatusMap', 'false');
    setLiveStatus(false);
    localStorage.setItem('onlyBoundMap', 'false');
    setOnlyBound(false);

    localStorage.removeItem('zonesChecked');

    setAlarmInfo([])

    if (fromMap) {
      setAlarmChecked(null)
    }

    const currListMaps: any = listMaps.length > 0 ? listMaps : mapsList

    let mapSelected = currListMaps?.find((map: any) => map.name == currMap)

    const updateMapSelected = mapSelected ?? currListMaps?.find((map: any) => map.id == currMap)

    if (updateMapSelected == null) return

    if ((deviceMacFromSynoptic && deviceMapIdFromSynoptic == updateMapSelected.id)) {
      setShowDeviceMacBySynoptic(true)
    } else {
      setShowDeviceMacBySynoptic(false)
    }
    if (beaconId && mapIdByBeacon == updateMapSelected.id) {
      setShowBeaconMac(true)
      if (beaconsFromGrid.length > 0) changeCheckboxBeacon({ target: { element: { checked: true } } }, beaconId, beaconsFromGrid);
    } else {
      setShowBeaconMac(false)
      changeCheckboxBeacon({ target: { element: { checked: false } } }, beaconId);
      // Rimuovi beaconId e mapIdByBeacon dal localStorage quando si cambia mappa
      if (mapIdByBeacon != null && mapIdByBeacon != updateMapSelected.id) {
        localStorage.removeItem('beaconId');
        localStorage.removeItem('mapIdByBeacon');
        setBeaconIdSelected(null);
        
      }
    }

    renderMap(updateMapSelected);
    await getMapById(updateMapSelected?.id) // inizialize the mapCanvas

    const isMapGeoStatic: any = localStorage.getItem('mapGeoreferenced') ? JSON.parse(localStorage.getItem('mapGeoreferenced') as string) : null
    const isGeoreferenced: any = isMapGeoStatic ?? updateMapSelected?.georeferenced == 1 ? true : false

    localStorage.setItem('mapGeoreferenced', JSON.stringify(isGeoreferenced))
    setToggleGeoStatic(isGeoreferenced);
    setMapGeoreferenced(isGeoreferenced)

    getZoneList(updateMapSelected.id, isGeoreferenced)

    getBeaconList(updateMapSelected.id, true, beaconsFromGrid.length > 0, isGeoreferenced, currListMaps, true)

    setSelectedMap(updateMapSelected)
    setMapName(updateMapSelected.name)

    setSelectedMap(updateMapSelected)
    localStorage.setItem('mapId', updateMapSelected.id)

    const mapCtx = mapCanvas?.current?.getContext("2d");
    const mapWidth = mapCanvas?.width ?? mapCanvas?.current?.width
    const mapHeight = mapCanvas?.height ?? mapCanvas?.current?.height
    mapCtx?.clearRect(0, 0, mapWidth, mapHeight)

    const zoneCtx = zoneCanvas?.current?.getContext("2d");
    const zoneWidth = zoneCanvas?.width ?? zoneCanvas?.current?.width
    const zoneHeight = zoneCanvas?.height ?? zoneCanvas?.current?.height
    zoneCtx?.clearRect(0, 0, zoneWidth, zoneHeight)

    // reset beacons from map
    changeCheckboxBeacon(false, 0)

    const alarmCtx = alarmCanvas?.current?.getContext("2d");
    const alarmWidth = alarmCanvas?.width ?? alarmCanvas?.current?.width
    const alarmHeight = alarmCanvas?.height ?? alarmCanvas?.current?.height
    alarmCtx?.clearRect(0, 0, alarmWidth, alarmHeight)

    wakeUpGroupByMapId(updateMapSelected.id, isGeoreferenced)
  }

  const getMapById = async (mapId: number) => {

    setLoading(true);

    const commonOperations = (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      axios.get(`${SERVER_URL}/map/${mapId}`, { headers })
        .then((res) => {

          if (res.data == '' || res.data.length == 0) {
            setLoading(false);
            return;
          }

          const data = res.data.data

          let infoMapSelected: any = []
          let botLeft: any = []
          let topLeft: any = []
          let botRight: any = []
          let topRight: any = []

          infoMapSelected.push(data)
          botLeft.push(data.botLeft)
          topLeft.push(data.topLeft)
          botRight.push(data.botRight)
          topRight.push(data.topRight)

          setMapName(data.name)

        })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        });
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
          console.log(error);
          setLoading(false);
        });
    }
    setLoading(false);
  }

  const getZoneList = async (mapId: number, isGeoreferenced?: boolean) => {

    if (mapId == null || mapId == undefined) return

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      axios.get(`${SERVER_URL}/zone/bymapid/${mapId}?isGeoreferenced=${isGeoreferenced}`, { headers })
        .then((res) => {
          const data = res?.data?.data

          let listZones: any = []
          let listZonesSelected: any = []
          let listZoneNames: any = []

          // add a checkbox All Zones
          listZones.push({
            id: '0',
            name: localizationService.toLanguageString('map.selectAll', 'NOT_FOUND'),
            color: 'white'
          })

          data?.forEach((zone: any, id: any) => {
            listZones.push(zone)
            listZoneNames.push(zone.name)
            listZonesSelected.push({
              id: zone.id,
              name: zone.name,
              color: zone.color,
              points: zone.points
            })
          });

          setZonesCheckboxByMap(listZones)
        })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
          console.log(error);
          setLoading(false);
          navigate("/")
        });
    }
    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        });
    }
  }

  const getGroupList = async () => {

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      axios.get(`${SERVER_URL}/group/list`, { headers })
        .then((res) => {
          
          const data = res.data.data.filter((item: any) => item.enabled !== false);

          localStorage.setItem('groupsList', JSON.stringify(data));

          //select all groups by default 
          data?.forEach((group: any) => {
            groupsCheckboxByMap.push(group)
          })

          let devicesToShowInMap = localStorage.getItem('devicesList')
          devicesToShowInMap = groupsCheckboxByMap ?? devicesToShowInMap

          setDeviceChecked(devicesToShowInMap)
        })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        });
    }
    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
          console.log(error);
        });
    }
  }

  const getBeaconList = async (mapId: number, flag: boolean, fromBeaconGrid?: boolean, isMapGeoreferenced?: any, mapsList?: any, toggleGeoStatic?: any) => {

    setLoading(true);

    if (mapId == null || mapId == undefined) return

    const convertGeoMap = isMapGeoreferenced == '1' ? true : false

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      const params = {
        enabled: flag,
        isGeoreferenced: convertGeoMap
      };
      await axios.get(`${SERVER_URL}/beacon/${mapId}`, { headers, params })
        .then((res) => {
          const data = res?.data?.data

          let beaconsByMapId: any = []

          // add a checkbox All Beacons
          beaconsByMapId.push({
            id: '0',
            name: localizationService.toLanguageString('map.selectAll', 'NOT_FOUND'),
            color: 'white',
            checked: false
          })

          data?.forEach((beacon: any) => {
            if (localStorage.getItem('beaconId') == beacon.id) {
              beaconsByMapId.push(beacon)
              beacon.checked = false
              return
            } else {
              if (beacon.mapId != mapId) {
                return;
              } else {
                beaconsByMapId.push(beacon)
              }
              beacon.checked = false
            }
          });

          if (flag) {
            // format date in data.data
            beaconsByMapId.forEach((beacon: any) => {
              if (beacon?.lastSeen == null) {
                beacon.lastSeen = localizationService.toLanguageString('beacons.date_not_found', 'NOT_FOUND')
                return
              }
              beacon.lastSeen = beacon?.lastSeen?.split('T')[0]

              //format alarm.dateTime with gg/mm/yyyy
              const date = new Date(beacon?.lastSeen);
              const day = date?.getDate();

              const month = (date.getMonth() + 1).toString().padStart(2, "0");
              const year = date.getFullYear();
              beacon.lastSeen = new Date(`${year},${month},${day}`)
            })
            setListBeaconsOn(beaconsByMapId);
            setAllBeacons(beaconsByMapId);
          }

          if (fromBeaconGrid) {
            beaconsByMapId.forEach((beacon: any) => {
              if (beacon.id == beaconId) {
                beacon.checked = true
                getMapByPosition(beacon.longitude, beacon.latitude, beacon.elevation, null, mapsList)
                beaconsByMapId.shift()
                const beaconFromGrid = beaconsByMapId.filter((b: any) => beacon.id.includes(b.id))
                setSelectedBeacons(beaconFromGrid)
                if (beaconFromGrid.length > 0) localStorage.setItem('beaconFromGrid', JSON.stringify(beaconFromGrid))
              }
            });
            setBeaconsFromGrid(beaconsByMapId)
            changeCheckboxBeacon(true, beaconId, beaconsByMapId)
          } else {
            changeCheckboxBeacon(true, beaconId, beaconsByMapId)
            setSelectedBeacons([])
          }

          if (!toggleGeoStatic && !fromBeaconGrid) {
            const updatedListBeaconCheckbox = beaconsByMapId.map((beacon: any) => {
              return {
                ...beacon, // Create a new object based on the existing beacon
                checked: false, // Override the checked property
              };
            })
            setListBeaconsOn(updatedListBeaconCheckbox)
            setAllBeacons(updatedListBeaconCheckbox)
          }

        }).catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
          console.log(error);
          setLoading(false);
        });
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
          console.log(error);
          setLoading(false);
        });
    }
  }

  const getMapByPosition = async (positionX: any, positionY: any, elevation: any, orientation?: any, mapsList?: any) => {

    setLoading(true)

    const params: any = {
      longitude: positionX,
      latitude: positionY,
      elevation: elevation,
      ...(orientation != null && { orientation })
    }

    const commonOperations = (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      axios.post(`${SERVER_URL}/map/position`, params, { headers })
        .then(async (res) => {
          const data = res.data.data

          if (data?.length == 0) return

          const mapInfo = data
          await changeMap(mapInfo, false, mapsList)
        })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
          console.log(error);
          setLoading(false)
        });
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
          console.log(error);
          setLoading(false)
        });
    }
  }

  const processMarkersFromData = (data: any, mapId: any, isGeoreferenced?: any) => {
    let devicesList: any = data;

    const isLive = localStorage.getItem('liveStatusMap');
    const onlyBound = localStorage.getItem('onlyBoundMap');

    const currDeviceMacFromSynoptic = localStorage.getItem('deviceMacFromSynoptic');

    if (currDeviceMacFromSynoptic != null && deviceMapIdFromSynoptic == mapId) {
      devicesList = devicesList.filter((device: any) => device.deviceMac == currDeviceMacFromSynoptic);
    }

    if (isLive == 'true') {
      const minutesBefore: any = localStorage.getItem('statusDevice') ?? 75;
      let liveDevicesToShowInMap: any = [];
      let dateBeforeMinutes: any = '';

      devicesList?.map((device: any) => {
        if (device.dateTime == null || device.dateTime == 0) {
          return;
        } else {
          dateBeforeMinutes = getDateRange(minutesBefore);
          device.dateTime = convertDateTime(device?.dateTime);
          const localTimeString = device.dateTime.toLocaleString(languageId, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          });
          if (compareDateTimes(localTimeString, dateBeforeMinutes) == 1) {
            liveDevicesToShowInMap.push(device);
          }
        }
      });

      setDeviceChecked(liveDevicesToShowInMap);
      localStorage.setItem('devicesLive', JSON.stringify(liveDevicesToShowInMap));

      const devicesLive = JSON.parse(localStorage.getItem("devicesLive") || "[]").map((d: any) => d?.mac ?? d.deviceMac);
      devicesList = devicesList.filter((device: any) =>
        devicesLive.includes(device?.mac ?? device.deviceMac)
      );
      localStorage.setItem('devicesLive', JSON.stringify(devicesList));
    }

    const devicesWithName = devicesList.filter(
      (device: any) => device.name && device.name.trim() !== ''
    );

    if (onlyBound == 'true') {
      devicesList = devicesWithName;
      localStorage.setItem('devicesList', JSON.stringify(devicesWithName));
    } else {
      localStorage.setItem('devicesList', JSON.stringify(data));
    }

    setDeviceChecked(data);
    localStorage.setItem('devicesChecked', JSON.stringify(data));

    const groupsCheckboxByMap: any = JSON.parse(localStorage.getItem('groupsCheckboxByMap') ?? '[]');
    const groupsCheckedIds = groupsCheckboxByMap.filter((item: any) => item.checked != false).map((item: any) => item.id);

    let devicesToShowInGeoMap: any = [];
    const newSVGMarkers: any = [];
    const scale = transformState?.scale ?? 1;

    devicesList.forEach((device: any) => {
      if (groupsCheckedIds.includes(device.groupId)) {
        const coords = getLatLong(device?.position);
        if (!coords) {
          return;
        }

        if (!isGeoreferenced) {
          const projectedPoint = projectPointOnCanvas(device?.position, mapImageWidth, mapImageHeight);
          if (!projectedPoint) {
            return;
          }

          const x = Math.round(projectedPoint.x) + 4.5;
          const y = Math.round(projectedPoint.y) - 4;
          
          // Dimensioni fisse del marker
          const baseMarkerRadius = 16;
          const baseHoleRadius = 12;
          const offset = 4;
                          
          const angleOffset = Math.PI / 6;
          const leftX = x - offset - Math.sin(angleOffset) * baseMarkerRadius;
          const leftY = y - offset - baseMarkerRadius + Math.cos(angleOffset) * baseMarkerRadius;
          const rightX = x - offset + Math.sin(angleOffset) * baseMarkerRadius;
          const rightY = y - offset - baseMarkerRadius + Math.cos(angleOffset) * baseMarkerRadius;
          
          newSVGMarkers.push({
            x: x - offset,
            y: y - offset - baseMarkerRadius,
            markerRadius: baseMarkerRadius,
            holeRadius: baseHoleRadius,
            leftX,
            leftY,
            rightX,
            rightY,
            tipX: x - offset,
            tipY: y,
            color: device?.groupColor ?? 'lightgrey',
            deviceMac: device?.mac ?? device?.deviceMac,
            deviceName: device?.name,
            scale: 1 / scale
          });
          
          devicesToShowInGeoMap.push({
            id: device?.id,
            mac: device?.mac ?? device?.deviceMac,
            name: device?.name,
            groupId: device?.groupId,
            color: device?.groupColor,
            checked: true,
            position: {
              latitude: coords.latitude,
              longitude: coords.longitude,
              elevation: device?.position?.elevation,
              orientation: device?.position?.orientation
            }
          });

        } else {
          devicesToShowInGeoMap.push({
            id: device?.id,
            mac: device?.mac ?? device?.deviceMac,
            name: device?.name,
            groupId: device?.groupId,
            color: device?.groupColor,
            checked: true,
            position: {
              latitude: coords.latitude,
              longitude: coords.longitude,
              elevation: device?.position?.elevation,
              orientation: device?.position?.orientation
            }
          });
        }
      }
    });
    
    localStorage.setItem('devicesChecked', JSON.stringify(devicesToShowInGeoMap));
    
    // Aggiorna i marker SVG
    setGroupMarkersSVG(newSVGMarkers);
    
    // Nascondi il loading
    setLoading(false);
    isChangingMapRef.current = false;
  };

  const wakeUpGroupByMapId = async (mapId: any, isGeoreferenced?: any, onlyBound?: any, fromSwitchOnlyBound?: any) => {

    if (mapId == null) return

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      axios.get(`${SERVER_URL}/map/${mapId}/wakeups?isGeoreferenced=${isGeoreferenced}`, { headers })
        .then((res) => {
          const data = res?.data?.data

          const devicesList: any = []
          const deviceGroupId: any = []

          const groupsCheckedList: any = JSON.parse(localStorage.getItem('groupsCheckboxByMap') as string)

          data.forEach((device: any) => {
            deviceGroupId.push(device.groupId)

            if (onlyBound) {
              groupsCheckedList.forEach((group: any) => {
                if (group.id == device.groupId) {
                  if (device?.name == '' || device?.name == null) {
                    return
                  }
                }
              })
            }

            devicesList.push(device)
          });

          localStorage.setItem('devicesList', JSON.stringify(devicesList))

          const groupsCheckboxByMap: any = []
          const groupsList = JSON.parse(localStorage.getItem('groupsList') as string)

          // add a checkbox "Select all"
          groupsCheckboxByMap.push({
            id: '0',
            name: localizationService.toLanguageString('map.selectAll', 'NOT_FOUND'),
            color: 'white'
          })

          // add checkbox Not assigned
          groupsCheckboxByMap.push({
            id: null,
            name: localizationService.toLanguageString('map.noGroup', 'NOT_FOUND'),
            color: 'lightgrey'
          })


          // populate the checkbox group
          groupsList?.forEach((group: any) => {
            if (deviceGroupId.includes(group.id)) {
              groupsCheckboxByMap.push({
                id: group?.id,
                name: group?.name,
                color: group?.color,
              })
            }
          })

          groupsCheckboxByMap.forEach((group: any) => {
            group.checked = true
          });

          localStorage.setItem('groupsCheckboxByMap', JSON.stringify(groupsCheckboxByMap));

          if (!fromSwitchOnlyBound) {
            setGroupsCheckboxByMap(groupsCheckboxByMap)
          }

          // Process markers directly with data already fetched
          processMarkersFromData(data, mapId, isGeoreferenced);

          if (alarmChecked) changeAlarm(alarmChecked.checked)

        })
        .catch((error) => {
          setLoading(false);
          isChangingMapRef.current = false;
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        });
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        });
    }
  }

  const refreshGroupPositionsInMap = async (mapId: any, isGeoreferenced?: any) => {

    setLoading(true)

    setShowTooltip(false)

    if (mapId == null || mapId == '' || mapId == undefined) return

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      await axios.get(`${SERVER_URL}/map/${mapId}/wakeups?isGeoreferenced=${isGeoreferenced}`, { headers })
        .then((res) => {

          const data = res?.data?.data

          let devicesList: any = data

          const isLive = localStorage.getItem('liveStatusMap')
          const onlyBound = localStorage.getItem('onlyBoundMap')

          const currDeviceMacFromSynoptic = localStorage.getItem('deviceMacFromSynoptic');

          if (currDeviceMacFromSynoptic != null && deviceMapIdFromSynoptic == mapId) {
            devicesList = devicesList.filter((device: any) => device.deviceMac == currDeviceMacFromSynoptic)
          }

          if (isLive == 'true') {

            const minutesBefore: any = localStorage.getItem('statusDevice') ?? 75 // default value in hour    

            let liveDevicesToShowInMap: any = []
            let dateBeforeMinutes: any = ''

            devicesList?.map((device: any) => {
              if (device.dateTime == null || device.dateTime == 0) {
                return
              } else {
                dateBeforeMinutes = getDateRange(minutesBefore)
                device.dateTime = convertDateTime(device?.dateTime);
                const localTimeString = device.dateTime.toLocaleString(languageId, {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  //hour12: true // Force 12-hour format with AM/PM
                });
                if (compareDateTimes(localTimeString, dateBeforeMinutes) == 1) {
                  liveDevicesToShowInMap.push(device)
                }
              }
            })

            setDeviceChecked(liveDevicesToShowInMap)
            localStorage.setItem('devicesLive', JSON.stringify(liveDevicesToShowInMap))

            const devicesLive = JSON.parse(localStorage.getItem("devicesLive") || "[]").map((d: any) => d.deviceMac);
            devicesList = devicesList.filter((device: any) =>
              devicesLive.includes(device.deviceMac)
            );
            localStorage.setItem('devicesLive', JSON.stringify(devicesList))
          }

          const devicesWithName = devicesList.filter(
            (device: any) => device.name && device.name.trim() !== ''
          );

          if (onlyBound == 'true') {
            devicesList = devicesWithName
            localStorage.setItem('devicesList', JSON.stringify(devicesWithName))
          } else {
            localStorage.setItem('devicesList', JSON.stringify(data))
          }

          setDeviceChecked(data)
          localStorage.setItem('devicesChecked', JSON.stringify(data))

          const groupsCheckboxByMap: any = JSON.parse(localStorage.getItem('groupsCheckboxByMap') ?? '[]');
          const groupsCheckedIds = groupsCheckboxByMap.filter((item: any) => item.checked != false).map((item: any) => item.id);

          let devicesToShowInGeoMap: any = []
          const newSVGMarkers: any = []
          const scale = transformState?.scale ?? 1;

          devicesList.forEach((device: any) => {
            if (groupsCheckedIds.includes(device.groupId)) {
              const coords = getLatLong(device?.position);
              if (!coords) {
                return;
              }

              if (!isGeoreferenced) {
                const projectedPoint = projectPointOnCanvas(device?.position, mapImageWidth, mapImageHeight);
                if (!projectedPoint) {
                  return;
                }

                // Coordinate sulla mappa (il TransformComponent si occupa della trasformazione)
                const x = Math.round(projectedPoint.x) + 4.5;
                const y = Math.round(projectedPoint.y) - 4;
                
                // Dimensioni fisse del marker
                const baseMarkerRadius = 16;
                const baseHoleRadius = 12;
                const offset = 4;
                                
                // Calcola i punti dove la punta si attacca al cerchio (ai lati)
                const angleOffset = Math.PI / 6; // 30 gradi
                const leftX = x - offset - Math.sin(angleOffset) * baseMarkerRadius;
                const leftY = y - offset - baseMarkerRadius + Math.cos(angleOffset) * baseMarkerRadius;
                const rightX = x - offset + Math.sin(angleOffset) * baseMarkerRadius;
                const rightY = y - offset - baseMarkerRadius + Math.cos(angleOffset) * baseMarkerRadius;
                
                // Create SVG marker data
                newSVGMarkers.push({
                  x: x - offset,
                  y: y - offset - baseMarkerRadius,
                  markerRadius: baseMarkerRadius,
                  holeRadius: baseHoleRadius,
                  leftX,
                  leftY,
                  rightX,
                  rightY,
                  tipX: x - offset,
                  tipY: y,
                  color: device?.groupColor ?? 'lightgrey',
                  deviceMac: device?.deviceMac,
                  deviceName: device?.name,
                  scale: 1 / scale
                });
                
                devicesToShowInGeoMap.push({
                  id: device?.id,
                  mac: device?.deviceMac,
                  name: device?.name,
                  groupId: device?.groupId,
                  color: device?.groupColor,
                  checked: true,
                  position: {
                    latitude: coords.latitude,
                    longitude: coords.longitude,
                    elevation: device?.position?.elevation,
                    orientation: device?.position?.orientation
                  }
                })

              } else {
                devicesToShowInGeoMap.push({
                  id: device?.id,
                  mac: device?.deviceMac,
                  name: device?.name,
                  groupId: device?.groupId,
                  color: device?.groupColor,
                  checked: true,
                  position: {
                    latitude: coords.latitude,
                    longitude: coords.longitude,
                    elevation: device?.position?.elevation,
                    orientation: device?.position?.orientation
                  }
                })
              }
            }
          })
          localStorage.setItem('devicesChecked', JSON.stringify(devicesToShowInGeoMap))
          
          // Update SVG markers - non viene usato isWaitingForMarkersRef qui perché è per il refresh periodico
          setGroupMarkersSVG(newSVGMarkers)
          setLoading(false)
          isChangingMapRef.current = false
        })
        .catch((error) => {
          setLoading(false);
          isChangingMapRef.current = false;
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        });
    }
    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
        });
    }
  }

  const changeZone = (ev: any, zoneId: any) => {
    let checked: any = ev.target.element.checked;
    let zonesCheckboxListByMap: any[] = zonesCheckboxByMap
    let zonesChecked: any[] = localStorage.getItem('zonesChecked') ? JSON.parse(localStorage.getItem('zonesChecked') as string) : []

    let zoneCtx: any;

    //manage checkbox "selectall"
    if (zoneId == 0) {
      if (!checked) {
        const updatedZones = zonesCheckboxByMap.map((zone: any) => ({
          ...zone,
          checked: false
        }));

        setZoneCheckedList(updatedZones);
        setZonesCheckboxByMap(updatedZones);
        zonesChecked = updatedZones;

        if (zoneCanvas.current != null || currentZoneCanvas.current != null) {
          zoneCtx = zoneCanvas?.current?.getContext("2d") ?? currentZoneCanvas?.current?.getContext("2d");
          zoneCtx.clearRect(0, 0, zoneCanvas.current.width, zoneCanvas.current.height);
        }

        localStorage.setItem('zonesChecked', JSON.stringify([]));
      } else {
        const updatedZones = zonesCheckboxListByMap.map((zone: any) => ({
          ...zone,
          checked: true
        }));

        setZoneCheckedList(updatedZones);
        setZonesCheckboxByMap(updatedZones);
        zonesChecked = updatedZones;

        localStorage.setItem('zonesChecked', JSON.stringify(updatedZones));
      }
    }

    //manage single checkbox
    if (zoneId != 0) {
      if (!checked) {
        zonesCheckboxByMap.map((zone: any) => {
          if (zone.id == zoneId) {
            zone.checked = false
            //zonesChecked.splice(zoneId, 1);  // Removes 1 element at the found index
          }
        })
        zonesChecked = zonesChecked.filter(item => item.id !== zoneId);
      } else {
        zonesCheckboxByMap.map((zone: any) => {
          if (zone.id == zoneId) {
            zone.checked = true
            zonesChecked.push(zone)
          }
        })
      }
      setZonesCheckboxByMap(zonesCheckboxByMap)
      setZoneCheckedList(zonesChecked)
      localStorage.setItem('zonesChecked', JSON.stringify(zonesChecked));

      const updatedZones = zonesCheckboxListByMap.map((zone: any) => ({ ...zone }));

      const allCheckedExceptZero = updatedZones.filter(z => z.id !== '0').every(z => z.checked === true);
      const allUncheckedExceptZero = updatedZones.filter(z => z.id !== '0').some(z => z.checked === false);

      if (allCheckedExceptZero) {
        const zoneZero = updatedZones.find(z => z.id === '0');
        if (zoneZero) {
          zoneZero.checked = true; // deseleziona zoneId = 0
        }
        setZonesCheckboxByMap(updatedZones);
      }
      if (allUncheckedExceptZero) {
        const zoneZero = updatedZones.find(z => z.id === '0');
        if (zoneZero) {
          zoneZero.checked = false; // deseleziona zoneId = 0
        }
        setZonesCheckboxByMap(updatedZones);
      }

    }

    if (zoneCanvas.current != null || currentZoneCanvas.current != null) {
      zoneCtx = zoneCanvas?.current?.getContext("2d") ?? currentZoneCanvas?.current?.getContext("2d");
      zoneCtx.clearRect(0, 0, zoneCanvas.current.width, zoneCanvas.current.height)
    }

    // re-render the zone canvas after deleting a zone deselected
    if (zonesChecked.length > 0 && !mapGeoreferenced) {
      zonesChecked.forEach((zone: any) => {
        if (zone.id != 0 && zone.checked) {
          zoneCtx.beginPath();
          zoneCtx.fillStyle = zone.color
          zoneCtx.moveTo(zoneCanvas.current.width * zone.points[0].longitude, zoneCanvas.current.height - zoneCanvas.current.height * zone.points[0].latitude);
          zone.points.forEach((points: any) => {
            zoneCtx.lineTo(zoneCanvas.current.width * points.longitude, zoneCanvas.current.height - (zoneCanvas.current.height * points.latitude));
          });
          zoneCtx.globalAlpha = 0.8;
          zoneCtx.fill();
        }
      });
    }

  };

  const changeGroup = (ev?: any, groupId?: any, isMapGeoreferenced?: boolean) => {

    let checked: any = ev?.target?.element?.checked ?? ev;

    let devicesList: any = JSON.parse(localStorage.getItem('devicesList') as string)
    let groupsCheckboxByMap: any = JSON.parse(localStorage.getItem('groupsCheckboxByMap') as string)

    let groupsCheckboxListByMap: any = groupsCheckboxByMap

    let devicesChecked: any = []
    let groupCheckedLive: any = []
    if (localStorage.getItem('liveStatusMap') == 'true') {
      groupCheckedLive = JSON.parse(localStorage.getItem('devicesLive') || '[]')
    } else {
      devicesChecked = localStorage.getItem('devicesChecked') != null && localStorage.getItem('devicesChecked') != '[]' ? JSON.parse(localStorage.getItem('devicesChecked') as string) : devicesList
    }

    //manage checkbox "selectall"
    if (groupId == 0) {
      if (!checked) {
        devicesChecked = []
        groupsCheckboxListByMap?.forEach((group: any) => {
          group.checked = false
        });
      } else {
        if (liveStatusMap == true) {
          groupsCheckboxListByMap?.map((group: any) =>
            group.checked = false
          );
        }
        groupsCheckboxListByMap?.forEach((group: any) => {
          group.checked = true
        });
        devicesChecked = devicesList
      }
      setGroupsCheckboxByMap(groupsCheckboxListByMap)
      localStorage.setItem('devicesChecked', devicesChecked)
      localStorage.setItem('groupsCheckboxByMap', JSON.stringify(groupsCheckboxListByMap));
    }

    //manage single checkbox
    if (groupId != 0) {
      if (devicesList.length > 0) {
        const groupIds = devicesList.map((device: any) => device.groupId);
        const exists = groupIds.includes(groupId);
        if (exists) {
          devicesList.forEach((group: any) => {
            if (group?.groupId == groupId) {
              groupsCheckboxByMap.forEach((g: any) => {
                if (g.id === groupId) {
                  g.checked = checked;
                }
              });
            }
          });
        } else {
          groupsCheckboxByMap.forEach((group: any) => {
            if (group.id == groupId) {
              group.checked = checked
            }
          })
        }
      } else {
        const groupIds = groupsCheckboxByMap.map((device: any) => device.groupId);
        const exists = groupIds.includes(groupId);
        if (exists) {
          groupsCheckboxByMap.forEach((group: any) => {
            if (group?.groupId == groupId) {
              group.checked = checked;
            }
          });
        } else {
          groupsCheckboxByMap.forEach((group: any) => {
            if (group.id == groupId) {
              group.checked = checked
            }
          })
        }
      }

      setGroupsCheckboxByMap(groupsCheckboxByMap)

      const updatedGroups = groupsCheckboxListByMap.map((group: any) => ({ ...group }));

      const allCheckedExceptZero = updatedGroups.filter((z: any) => z.id !== '0').every((z: any) => z.checked === true);
      const allUncheckedExceptZero = updatedGroups.filter((z: any) => z.id !== '0').some((z: any) => z.checked === false);

      if (allCheckedExceptZero) {
        const zoneZero = updatedGroups.find((z: any) => z.id === '0');
        if (zoneZero) {
          zoneZero.checked = true;
          updatedGroups[0].checked = true
        }
      }
      if (allUncheckedExceptZero) {
        const zoneZero = updatedGroups.find((z: any) => z.id === '0');
        if (zoneZero) {
          zoneZero.checked = false;
          updatedGroups[0].checked = false
        }
      }
      setGroupsCheckboxByMap(updatedGroups);

      localStorage.setItem('groupsCheckboxByMap', JSON.stringify(updatedGroups));
    }

    const checkedIds = groupsCheckboxListByMap.filter((item: any) => item.checked != false).map((item: any) => item.id);
    let updatedGroupChecked: any = []

    if (!isMapGeoreferenced) {

      if (liveStatusMap == true) {
        const groupIds = new Set(groupsCheckboxByMap.filter((g: any) => g.checked === true).map((g: any) => g.id));
        if (localStorage.getItem('onlyBoundMap') == 'true') {
          groupCheckedLive = groupCheckedLive.filter((item: any) =>
            item.name != ''
          )
        }
        const commonItems = groupCheckedLive.filter((item: any) =>
          groupIds.has(item.groupId)
        );

        commonItems.forEach((device: any) => {
          updatedGroupChecked.push(device)
        });
      }
      else {
        // Filtra i dispositivi in base ai gruppi selezionati
        if (devicesList.length > 0 && !mapGeoreferenced) {
          devicesList.forEach((device: any) => {
            if (deviceMacFromSynoptic != null && device.deviceMac == deviceMacFromSynoptic && checkedIds.includes(device.groupId)) {
              if (localStorage.getItem('onlyBoundMap') == 'true') {
                if (device?.name == '' || device?.name == null) {
                  return
                }
              }
              updatedGroupChecked.push(device)
            }
            else if (deviceMacFromSynoptic == null && checkedIds.includes(device.groupId)) {
              if (localStorage.getItem('onlyBoundMap') == 'true') {
                if (device?.name == '' || device?.name == null) {
                  return
                }
              }
              updatedGroupChecked.push(device)
            }
          });
        }
      }
      setDeviceChecked(updatedGroupChecked)
      localStorage.setItem('devicesChecked', JSON.stringify(updatedGroupChecked));
      
      // Aggiorna i marker SVG direttamente dai dispositivi filtrati
      const scale = transformState?.scale ?? 1;
      const newSVGMarkers: any = [];
      const offset = 4;
      
      updatedGroupChecked.forEach((device: any) => {
        const projectedPoint = projectPointOnCanvas(device?.position, mapImageWidth, mapImageHeight);
        if (!projectedPoint) {
          return;
        }

        const x = Math.round(projectedPoint.x) + 4.5;
        const y = Math.round(projectedPoint.y) - 4;
        
        // Dimensioni fisse del marker
        const baseMarkerRadius = 16;
        const baseHoleRadius = 12;
        
        const angleOffset = Math.PI / 6;
        const leftX = x - offset - Math.sin(angleOffset) * baseMarkerRadius;
        const leftY = y - offset - baseMarkerRadius + Math.cos(angleOffset) * baseMarkerRadius;
        const rightX = x - offset + Math.sin(angleOffset) * baseMarkerRadius;
        const rightY = y - offset - baseMarkerRadius + Math.cos(angleOffset) * baseMarkerRadius;
        
        newSVGMarkers.push({
          x: x - offset,
          y: y - offset - baseMarkerRadius,
          markerRadius: baseMarkerRadius,
          holeRadius: baseHoleRadius,
          leftX,
          leftY,
          rightX,
          rightY,
          tipX: x - offset,
          tipY: y,
          color: device?.color ?? device?.groupColor ?? 'lightgrey',
          deviceMac: device?.deviceMac,
          deviceName: device?.name,
          scale: 1 / scale
        });
      });
      
      setGroupMarkersSVG(newSVGMarkers);
    }
    else {
      if (devicesList.length > 0 && mapGeoreferenced) {
        devicesList.forEach((device: any) => {
          if (checkedIds.includes(device.groupId)) {
            if (localStorage.getItem('onlyBoundMap') == 'true') {
              if (device?.name == '' || device?.name == null) {
                return
              }
            }
            updatedGroupChecked.push(device)
          }
        });
      }
      setDeviceChecked(updatedGroupChecked)
      localStorage.setItem('devicesChecked', JSON.stringify(updatedGroupChecked));
    }
  };

  // toggle checkbox beacon and render beacon in map as SVG
  const changeCheckboxBeacon = (ev: any, beaconIdSelected?: any, fromBeaconGrid?: []) => {

    let checked: any = ev?.target?.element?.checked ?? ev;
    const listBeaconChecked: any[] = []

    if (mapImageWidth === 0 || mapImageHeight === 0) {
      return;
    }

    const updatedBeaconsList: any = []

    if (beaconIdSelected == '0') { // select all
      listBeaconsOn.forEach((beacon: any) => {
        if (checked) {
          beacon.checked = true
          listBeaconChecked.push(beacon)
        }
        else {
          beacon.checked = false
        }
      });
      setListBeaconsOn(listBeaconsOn)
      localStorage.setItem('beaconsByMapId', JSON.stringify(listBeaconsOn))
    }
    else {
      if (fromBeaconGrid != null) {
        fromBeaconGrid.forEach((beacon: any) => {
          if (beacon.id == beaconIdSelected) {
            if (checked) {
              beacon.checked = true
              listBeaconChecked.push(beacon)
            } else {
              beacon.checked = false
            }
          }
        });
        setAllBeacons(fromBeaconGrid)
        localStorage.setItem('beaconsByMapId', JSON.stringify(fromBeaconGrid))
      }
      else {
        listBeaconsOn.forEach((beacon: any) => {
          if (beacon.id == beaconIdSelected) {
            if (checked) {
              beacon.checked = true
              listBeaconChecked.push(beacon)
            } else {
              beacon.checked = false
            }
          }
        });
        updatedBeaconsList.push(listBeaconsOn)
        setListBeaconsOn(listBeaconsOn)

        localStorage.setItem('beaconsByMapId', JSON.stringify(listBeaconsOn))
      }

      const allCheckedExceptZero = listBeaconsOn
        .filter((b: any) => b.id !== '0')
        .every((b: any) => b.checked === true);

      const anyUncheckedExceptZero = listBeaconsOn
        .filter((b: any) => b.id !== '0')
        .some((b: any) => b.checked === false);

      const beaconZero = listBeaconsOn.find((b: any) => b.id === '0');
      if (beaconZero) {
        if (allCheckedExceptZero) {
          beaconZero.checked = true;
        } else if (anyUncheckedExceptZero) {
          beaconZero.checked = false;
        }
      }

      setListBeaconsOn(listBeaconsOn);

    }

    // Genera i marker SVG per i beacon
    const scale = transformState?.scale ?? 1;
    const newBeaconMarkers: any = [];
    const beaconList = fromBeaconGrid != null ? fromBeaconGrid : listBeaconsOn;
    
    beaconList.forEach((beacon: any) => {
      if (!beacon.checked) {
        return;
      }

      const projectedPoint = projectPointOnCanvas(beacon, mapImageWidth, mapImageHeight);
      if (!projectedPoint) {
        return;
      }

      newBeaconMarkers.push({
        x: projectedPoint.x,
        y: projectedPoint.y,
        color: beacon.color,
        name: beacon.name,
        type: beacon.type,
        id: beacon.id,
        scale: 1 / scale
      });
    });
    
    setBeaconMarkersSVG(newBeaconMarkers);
  };

  // Gestisce beaconId da localStorage
  useEffect(() => {
    if (beaconId && mapIdByBeacon && allBeacons.length > 0) {
      const beaconFromStorage: any = allBeacons.find((b: any) => b.id == beaconId);
      if (beaconFromStorage && !beaconFromStorage.checked) {
        beaconFromStorage.checked = true;
        setSelectedBeacons([beaconFromStorage]);
        changeCheckboxBeacon({ target: { element: { checked: true } } }, beaconId);
      }
    }
  }, [beaconId, mapIdByBeacon, allBeacons]);

  const onChangeBeacon = (event: any) => {
    // Se event.value è undefined o vuoto, significa che è stata fatta una deselezione
    if (!event?.value || event.value.length === 0) {
      // Deseleziona tutti i beacon
      selectedBeacons.forEach((beacon: any) => {
        changeCheckboxBeacon({ target: { element: { checked: false } } }, beacon.id);
      });
      setSelectedBeacons([]);
      localStorage.removeItem('beaconId');
      localStorage.removeItem('mapIdByBeacon');
      return;
    }

    const newSelectedIds = event.value.map((b: any) => b.id);
    const newSelected = listBeaconsOn.filter((b: any) => newSelectedIds.includes(b.id));

    const added = newSelected.filter((b: any) => !selectedBeacons.some((s: any) => s.id === b.id));
    const removed = selectedBeacons.filter((s: any) => !newSelected.some((b: any) => b.id === s.id));

    added.forEach((beacon: any) => {
      changeCheckboxBeacon({ target: { element: { checked: true } } }, beacon.id);
    });

    removed.forEach((beacon: any) => {
      changeCheckboxBeacon({ target: { element: { checked: false } } }, beacon.id);
    });

    if (removed[0]?.id == '0') {
      setSelectedBeacons([]);
    } else {
      setSelectedBeacons(newSelected);
    }

    // Pulisci localStorage beaconId solo se viene cambiata la selezione manualmente
    if (localStorage.getItem('beaconId') != null && removed.length > 0) {
      localStorage.removeItem('beaconId');
      localStorage.removeItem('mapIdByBeacon');
    }
  };

  useEffect(() => {
    const initiallyChecked = listBeaconsOn.filter((b: any) => b.checked && b.id !== '0');
    setSelectedBeacons(initiallyChecked);
  }, [listBeaconsOn]);

  const customFilterBeacons = (data: any[], filterText: string) => {
    const lowerFilter = filterText.toLowerCase();
    return data.filter(item =>
      (item.name && item.name.toLowerCase().includes(lowerFilter)) ||
      (item.id && item.id.toString().toLowerCase().includes(lowerFilter))
    );
  };

  const filterChange2 = (event: any) => {
    const filterText = event.filter?.value || '';
    if (!filterText) {
      // reset filter
      setListBeaconsOn(allBeacons);
      return;
    }
    const filtered = filterText ? customFilterBeacons(allBeacons, filterText) : allBeacons;
    setListBeaconsOn(filtered);

  };

  const tagRender = (tagData: any, tag: React.ReactElement) => {
    const item = tagData?.data[0];
    return (
      <span
        style={{
          width: '100%',
          whiteSpace: 'break-spaces',
          borderBottom: '1px solid #ccc'
        }}>
        {item?.id != 0 ? (
          <>
            {item.id} <br /> {item.name}
          </>
        ) : (
          item.name
        )}
      </span>
    );
  };

  const filterChange = (event: any) => {
    const filtered = filterBy(allBeacons.slice(), event.filter);
    setListBeaconsOn(filtered);
  };

  const beaconsRenderList = (li: React.ReactElement<HTMLLIElement>, itemProps: any) => {
    const item = itemProps?.dataItem;
    if (!item) return li;

    return (
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox
          name={`${item?.id} - ${item?.name}`}
          size={'small'}
          onChange={(e: any) => {
            changeCheckboxBeacon(e, item?.id)
            filterChange(e)
          }}
          label={
            <div style={{ borderBottom: '1px solid #ccc' }}>
              {item?.id != 0 ? item?.name : ''}
            </div>
          }
          checked={item?.checked}
        >
          <label className="checkbox-label-beacon" style={{ background: item?.color }} />
          {
            item?.id == 0 &&
            <label style={{ color: '#19838F', margin: '0 auto' }} >{item?.name}</label>
          }
          <label style={{ color: '#19838F', margin: '0 auto' }} >{item?.id != 0 ? item?.id : ''}</label>
        </Checkbox>
      </div>
    );
  };

  const changeAlarm = (ev: any) => {
    let checked: any = ev?.target?.element?.checked ?? ev;

    const alarmCtx = alarmCanvas?.current?.getContext("2d");
    const alarmWidth = 30;
    const alarmHeight = 18;

    if (alarmChecked != null) {
      if (checked) {
        alarmChecked.checked = true
        setAlarmToggle(true)
        localStorage.setItem("alarmInfoSelected", JSON.stringify(alarmChecked));
      }
      else {
        if (alarmChecked?.checked == true) {
          alarmChecked.checked = false
          alarmCtx.clearRect(0 - alarmWidth / 2, 0 - alarmHeight / 2, alarmCanvas.current.width + alarmWidth / 2, alarmCanvas.current.height + alarmHeight / 2)
        }
        setAlarmToggle(false)
        localStorage.removeItem('alarmInfoSelected');
      }

      if (alarmChecked.checked && alarmCtx != null) {
        alarmCtx.beginPath();
        alarmCtx.fillStyle = 'red'
        alarmCtx.lineWidth = 2;
        alarmCtx.strokeStyle = 'black';

        const width = alarmCanvas.current.width
        const height = alarmCanvas.current.height

        const x = width * alarmChecked?.info?.position?.longitude;
        const y = height - height * alarmChecked?.info?.position?.latitude;
        alarmCtx.fillRect(x - alarmWidth / 2, y - alarmHeight / 2, alarmWidth, alarmHeight);
        alarmCtx.strokeRect(x - alarmWidth / 2, y - alarmHeight / 2, alarmWidth, alarmHeight);
      }

    }
  };

  const getAlarmPositionByWakeupId = async (wakeupId: any, mapsList: any) => {

    setLoading(true);

    let languageId = localStorage.getItem('languageId');

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

      await axios.get(`${SERVER_URL}/wakeup/${wakeupId}`, { headers })
        .then((res) => {
          const data = res?.data.data

          setAlarmInfo(data)

          getMapByPosition(data.position.longitude, data.position.latitude, data.position.elevation, data.position.orientation, mapsList)

          setLoading(false);

        }).catch(() => {
          setLoading(false);
        });
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
    }
  }

  const getAlarmRelativePositionsById = async (alarmId: any, mapId: any) => {

    setLoading(true);

    let languageId = localStorage.getItem('languageId');

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

      await axios.get(`${SERVER_URL}/alarm/getAlarmRelativePositionsById?alarmId=${alarmId}&mapId=${mapId}`, { headers })
        .then((res) => {
          const data = res?.data.data

          setAlarmInfo(data)
          setAlarmChecked(data)

          setLoading(false);

        }).catch(() => {
          setLoading(false);
        });
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
    }
  }

  useEffect(() => {
    changeAlarm(alarmToggle)
  }, [alarmToggle])

  const mapRef: any = useRef(null);

  const getDateRange = (minutes: any) => {
    // Create a Date object from the input string
    const currentDate: any = new Date();

    // 2. Calculate milliseconds to subtract:
    const millisecondsToSubtract = minutes * 60 * 1000; // minutes * seconds * milliseconds

    // 3. Subtract the milliseconds:
    currentDate.setTime(currentDate?.getTime() - millisecondsToSubtract);

    const localTimeString = currentDate.toLocaleString(languageId, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      //hour12: true // Force 12-hour format with AM/PM
    });

    return localTimeString
  }

  const convertToDate = (dateString: string, isItalian: boolean = false): Date | null => {
    if (!dateString) return null;

    try {
      const cleanStr = dateString.replace(/,/g, '').trim();
      const parts = cleanStr.split(' ');

      let day: number, month: number, year: number;
      let hours: number = 0, minutes: number = 0, seconds: number = 0;

      if (isItalian) {
        // Formato italiano: "dd/MM/yyyy HH:mm:ss"
        const [datePart, timePart] = parts;
        if (!datePart || !timePart) return null;

        [day, month, year] = datePart.split('/').map(Number);
        [hours, minutes, seconds] = timePart.split(':').map(Number);
      } else {
        // Formato inglese: "MM/dd/yyyy hh:mm:ss AM|PM"
        if (parts.length < 3) return null;

        const [datePart, timePart, ampm] = parts;
        [month, day, year] = datePart.split('/').map(Number);
        [hours, minutes, seconds] = timePart.split(':').map(Number);

        if (ampm === 'PM' && hours < 12) hours += 12;
        if (ampm === 'AM' && hours === 12) hours = 0;
      }

      if ([day, month, year, hours, minutes, seconds].some(n => isNaN(n))) return null;

      const date = new Date(year, month - 1, day, hours, minutes, seconds);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  };

  const compareDateTimes = (date1Str: any, date2Str: any) => {

    let currLanguage: any = languageId
    if (currLanguage == 'it-IT') {
      currLanguage = true
    } else {
      currLanguage = false
    }
    // Parse the dates into Date objects    
    const date1: any = convertToDate(date1Str, currLanguage);
    const date2: any = convertToDate(date2Str, currLanguage);

    // Compare the milliseconds
    if (date1?.getTime() < date2?.getTime()) {
      return -1; // date1 is earlier
    } else if (date1?.getTime() > date2?.getTime()) {
      return 1; // date1 is later
    } else {
      return 0; // dates are equal
    }
  }

  const convertDateTime = (dateTime: any) => {

    if (dateTime == null) {
      return '-'
    }

    const [datePart, timePart] = dateTime?.split(' ');
    const [day, month, year] = datePart?.split('/');
    const [hour, minute, seconds] = timePart?.split(':');

    const date = new Date(Date.UTC(year, month - 1, day, hour, minute, seconds))

    return date
  }

  const onToggleLive = (e: any) => {

    const liveStatusMap: any = e.value ?? e
    setLiveStatus(liveStatusMap)
    localStorage.setItem('liveStatusMap', liveStatusMap)

    const onlyBoundMap = localStorage.getItem('onlyBoundMap')

    const storedGroupsChecked: any = localStorage.getItem('devicesChecked')
    const storedDevicesList: any = localStorage.getItem('devicesList')
    let currentMapId: any = JSON.parse(localStorage.getItem('mapId') as string)
    const groupsChecked = storedGroupsChecked != '' ? JSON.parse(storedGroupsChecked) : ''

    if (liveStatusMap) {
      const minutesBefore: any = localStorage.getItem('statusDevice') ?? 75 // default value in hour    

      const devicesList: any = JSON.parse(localStorage.getItem('devicesList') ?? '[]')

      let liveDevicesToShowInMap: any = []
      let dateBeforeMinutes: any = ''

      if (groupsChecked?.length == 0) return

      devicesList?.map((device: any) => {
        if (device.dateTime == null || device.dateTime == 0) {
          return
        } else {
          dateBeforeMinutes = getDateRange(minutesBefore)
          device.dateTime = convertDateTime(device?.dateTime);
          const localTimeString = device.dateTime.toLocaleString(languageId, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            //hour12: true // Force 12-hour format with AM/PM
          });
          if (compareDateTimes(localTimeString, dateBeforeMinutes) == 1) {
            liveDevicesToShowInMap.push(device)
          }
        }
      })

      if (onlyBoundMap == 'true') {
        liveDevicesToShowInMap = liveDevicesToShowInMap.filter((device: any) => device.name !== '');
      }
      setDeviceChecked(liveDevicesToShowInMap)
      localStorage.setItem('devicesLive', JSON.stringify(liveDevicesToShowInMap))
      //wakeUpGroupByMapId(currentMapId, liveStatusMap)

    } else {
      const devicesToShowInMap: any = JSON.parse(storedDevicesList)
      setDeviceChecked(devicesToShowInMap)
      localStorage.setItem('devicesChecked', JSON.stringify(devicesToShowInMap))
    }


    refreshGroupPositionsInMap(currentMapId, mapGeoreferenced)
  }

  const onToggleOnlyBound = (e: any) => {
    const onlyBound: any = e.value
    setOnlyBound(onlyBound)
    localStorage.setItem('onlyBoundMap', JSON.stringify(onlyBound))

    const groupsCheckboxByMap = JSON.parse(localStorage.getItem('groupsCheckboxByMap') as string)

    if (groupsCheckboxByMap != null) {
      refreshGroupPositionsInMap(selectedMap.id, mapGeoreferenced)
    }
  }

  const onToggleGeoStatic = (e: any) => {

    const toggleGeoStatic: any = e.value
    localStorage.setItem('mapGeoreferenced', JSON.stringify(toggleGeoStatic))
    setToggleGeoStatic(toggleGeoStatic)
    setMapGeoreferenced(toggleGeoStatic)

    const currMapSelected: any = selectedMap || '[]'
    const currMapId = currMapSelected.id
    renderMap(currMapSelected)

    if (toggleGeoStatic) {
      localStorage.removeItem('beaconId');
      localStorage.removeItem('mapIdByBeacon');
      localStorage.removeItem('beaconsByMapId');
      changeMap(currMapSelected.name, false)
      refreshGroupPositionsInMap(currMapId, true)
    } else {
      getZoneList(currMapId, false)
      getBeaconList(currMapId, true, false, false, null, false)
      changeMap(currMapSelected.name, false)
      refreshGroupPositionsInMap(currMapId, false)
      if (alarmInfoSelected != null) {
        getAlarmRelativePositionsById(alarmInfoSelected.id, currMapId)
      }
    }
  }

  const onToggleGrouping = (e: any) => {
    const groupingValue = Boolean(e?.value ?? e);
    setGroupingEnabled(groupingValue);
    localStorage.setItem('groupingEnabledMap', String(groupingValue));
  }

  useEffect(() => {
    if (lastMouseEvent) {
      handleUnifiedTooltip(lastMouseEvent);
    }
  }, [transformState]); // ogni volta che fai pan/zoom

  // Metodo unificato per gestire tutti i tooltip (marker SVG, beacon, alarm, devices)
  const handleUnifiedTooltip = (e: any) => {
    setLastMouseEvent(e);

    if (!transformContentRef.current) return;

    const { scale } = transformState;

    // Bounding box del contenitore trasformato
    const rect = transformContentRef.current.getBoundingClientRect();

    // Mouse position relativa al contenitore trasformato
    const mouseX = (e.clientX - rect.left) / scale;
    const mouseY = (e.clientY - rect.top) / scale;

    let tooltipStr = '';
    let found = false;
    let isBeaconTooltip = false;

    // PRIORITÀ 1: Verifica prima i marker SVG (hanno la precedenza)
    for (const marker of groupMarkersSVG) {
      const markerRadius = marker.markerRadius;
      const markerScale = marker.scale || 1;
      const tipX = marker.tipX;
      const tipY = marker.tipY;
      const circleX = marker.x;
      const circleY = marker.y;

      // Il marker viene scalato attorno al punto (marker.x, marker.tipY)
      // Trasforma le coordinate del mouse nello spazio scalato del marker
      const dx = mouseX - marker.x;
      const dy = mouseY - marker.tipY;
      
      // Inverti la scala per ottenere le coordinate del mouse nello spazio del marker originale
      const localMouseX = marker.x + (dx / markerScale);
      const localMouseY = marker.tipY + (dy / markerScale);
      
      // Ora verifica se localMouse è dentro il marker con dimensioni originali
      const distToCircle = Math.hypot(localMouseX - circleX, localMouseY - circleY);
      
      // Verifica il triangolo nello spazio originale
      const triangleHeight = tipY - circleY;
      const triangleBaseWidth = markerRadius * 1.6;
      const isInTriangle = localMouseY >= circleY && 
                           localMouseY <= tipY &&
                           localMouseX >= (tipX - (triangleBaseWidth / 2) * ((tipY - localMouseY) / triangleHeight)) &&
                           localMouseX <= (tipX + (triangleBaseWidth / 2) * ((tipY - localMouseY) / triangleHeight));

      if (distToCircle <= markerRadius || isInTriangle) {
        tooltipStr = `<div>${localizationService.toLanguageString('map.name', 'NOT_FOUND')}: ${marker.deviceName || ''}</div><div>Mac: ${marker.deviceMac}</div>`;
        found = true;
        break;
      }
    }

    // Se non ha trovato marker SVG dei dispositivi, cerca beacon SVG
    if (!found) {
      // Beacon SVG tooltips
      for (const beacon of beaconMarkersSVG) {
        const beaconWidth = 30;
        const beaconHeight = 18;
        const beaconScale = beacon.scale || 1;
        
        // Il beacon viene scalato attorno al suo centro (beacon.x, beacon.y)
        const dx = mouseX - beacon.x;
        const dy = mouseY - beacon.y;
        
        // Inverti la scala per ottenere le coordinate del mouse nello spazio del beacon originale
        const localMouseX = beacon.x + (dx / beaconScale);
        const localMouseY = beacon.y + (dy / beaconScale);
        
        // Verifica se il mouse è dentro il rettangolo del beacon
        const isInBeacon = localMouseX >= (beacon.x - beaconWidth / 2) &&
                          localMouseX <= (beacon.x + beaconWidth / 2) &&
                          localMouseY >= (beacon.y - beaconHeight / 2) &&
                          localMouseY <= (beacon.y + beaconHeight / 2);
        
        if (isInBeacon) {
          tooltipStr += `<div>${localizationService.toLanguageString('map.name', 'NOT_FOUND')}: ${beacon.name}</div><div>${localizationService.toLanguageString('map.type', 'NOT_FOUND')}: ${beacon.type}</div>`;
          found = true;
          isBeaconTooltip = true;
          break;
        }
      }

      // Alarm tooltips
      if (!found && alarmChecked != null && alarmChecked.checked) {
        const radius = 10;
        const groupX = alarmChecked?.info?.position?.longitude * mapImageWidth;
        const groupY = mapImageHeight - alarmChecked?.info?.position?.latitude * mapImageHeight;
        const distance = Math.hypot(mouseX - groupX, mouseY - groupY);

        if (distance <= radius) {
          const localDate = new Date(alarmInfoSelected?.dateTime);
          const dateTime = localDate.toLocaleString();
          tooltipStr += `<div>${localizationService.toLanguageString('map.name', 'NOT_FOUND')}: ${alarmChecked.name}</div><div>${localizationService.toLanguageString('map.date_time', 'NOT_FOUND')}: ${dateTime}</div>`;
          found = true;
        }
      }
    }

    if (found) {
      setTooltipContent(tooltipStr);
      const yOffset = isBeaconTooltip ? -15 : -35;
      setTooltipPos({ x: e.clientX + 10, y: e.clientY + yOffset });
      setShowTooltip(true);
    } else {
      setShowTooltip(false);
      setTooltipPos({ x: 0, y: 0 });
      setTooltipContent('');
    }
  };

  return (

    <div
      className="flex-parent-element-maps"
    >
      <NotificationGroup
        style={{
          right: 0,
          bottom: 50,
          alignItems: "flex-start",
          flexWrap: "wrap-reverse",
        }}
      >
        <Fade enter={true} exit={true}>
          {noPositionFound && (
            <Notification
              type={{ style: "warning", icon: true }}
              closable={true}
              onClose={() => setNoPositionFound(false)}
            >
              <span>{localizationService.toLanguageString('map.no_position_found', 'NOT FOUND')}</span>
            </Notification>
          )}
        </Fade>
      </NotificationGroup>
      
      {loading && loadingPanel}
      
      <div
        className="flex-left-child-element canvas-container"
        style={{
          width: '75%',
          background: '#F7F7F7'
        }}
      >
        <div
          ref={mapRef}
          style={{
            height: '100%',
            background: '#F7F7F7',
            paddingBottom: '30px',
            display: mapGeoreferenced ? 'none' : 'block'
          }}
        >

          <TransformWrapper
            key={`transform-${mapName}-${initialScale}`}
            initialScale={initialScale}
            initialPositionX={0}
            initialPositionY={0}
            minScale={0.1}
            centerOnInit={true}
            centerZoomedOut={true}
            smooth={true}
            onTransformed={(_, state) => {
              setTransformState(state);
              if (lastMouseEvent) {
                handleUnifiedTooltip(lastMouseEvent);
              }
            }}
          >
            {({ zoomIn, zoomOut, resetTransform, centerView, zoomToElement, ...rest }) => (
              <>
                <Controls />
                {noMaps &&
                  // Mostra un messaggio o un altro contenuto se non ci sono dati
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <p>{localizationService.toLanguageString('map.no_map', 'NOT_FOUND')}</p>
                  </div>
                }
                {showTooltip && (
                  <div
                    ref={tooltipRef}
                    style={{
                      position: "fixed",
                      top: tooltipPos.y - 30,
                      left: tooltipPos.x + 35,
                      backgroundColor: "white",
                      border: "1px solid black",
                      padding: "8px",
                      fontSize: "14px",
                      pointerEvents: "none",
                      zIndex: 9999,
                      boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.3)",
                      borderRadius: "4px",
                      transform: "translate(-50%, -50%)" // questo centra automaticamente rispetto alla posizione top/left
                    }}
                    dangerouslySetInnerHTML={{ __html: tooltipContent }}
                  />
                )}

                <TransformComponent
                  wrapperStyle={{
                    width: '100%',
                    height: '100%'
                  }}
                  contentStyle={{
                    width: mapImageWidth,
                    height: mapImageHeight,
                    position: 'relative'
                  }}
                >
                  <div ref={transformContentRef} onMouseMove={handleUnifiedTooltip}>
                    {mapImageWidth > 0 && mapImageHeight > 0 && (
                      <>
                        <canvas
                          ref={mapCanvas}
                          className="mapCanvas"
                          width={mapImageWidth}
                          height={mapImageHeight}
                        />
                        <canvas
                          ref={zoneCanvas}
                          className="zoneCanvas"
                          width={mapImageWidth}
                          height={mapImageHeight}
                        />
                        <canvas
                          ref={alarmCanvas}
                          className="alarmCanvas"
                          width={mapImageWidth}
                          height={mapImageHeight}
                        />
                                               
                        {/* SVG layer for high-quality group markers */}
                        <svg
                          className="groupCanvasSVG"
                          width={mapImageWidth}
                          height={mapImageHeight}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            pointerEvents: 'none',
                            zIndex: 5
                          }}
                        >
                          {groupMarkersSVG.map((marker: any, idx: number) => {
                            // Usa dimensioni fisse e applica scale tramite transform
                            const dropRadius = marker?.markerRadius;
                            const centerY = marker?.y;
                            const bottomY = marker?.tipY;
                            const topY = centerY - dropRadius * 0.7;
                            const markerScale = marker?.scale || 1;
                            
                            return (
                              <g key={`marker-${idx}`} transform={`translate(${marker.x}, ${marker.tipY}) scale(${markerScale}) translate(${-marker.x}, ${-marker.tipY})`}>
                                {/* Forma a goccia colorata con dimensioni fisse */}
                                <path
                                  d={`
                                    M ${marker?.x} ${bottomY}
                                    C ${marker?.x - dropRadius * 0.4} ${bottomY - dropRadius * 1.2},
                                      ${marker?.x - dropRadius * 0.85} ${centerY - dropRadius * 0.2},
                                      ${marker?.x - dropRadius * 0.85} ${topY}
                                    C ${marker?.x - dropRadius * 0.85} ${topY - dropRadius * 0.3},
                                      ${marker?.x - dropRadius * 0.5} ${topY - dropRadius * 0.5},
                                      ${marker?.x} ${topY - dropRadius * 0.5}
                                    C ${marker?.x + dropRadius * 0.5} ${topY - dropRadius * 0.5},
                                      ${marker?.x + dropRadius * 0.85} ${topY - dropRadius * 0.3},
                                      ${marker?.x + dropRadius * 0.85} ${topY}
                                    C ${marker?.x + dropRadius * 0.85} ${centerY - dropRadius * 0.2},
                                      ${marker?.x + dropRadius * 0.4} ${bottomY - dropRadius * 1.2},
                                      ${marker?.x} ${bottomY}
                                    Z
                                  `}
                                  fill={marker?.color}
                                  stroke="black"
                                  strokeWidth="1"
                                />
                              </g>
                            );
                          })}
                        </svg>
                        
                        {/* SVG layer for beacon markers */}
                        <svg
                          className="beaconCanvasSVG"
                          width={mapImageWidth}
                          height={mapImageHeight}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            pointerEvents: 'none',
                            zIndex: 4
                          }}
                        >
                          {beaconMarkersSVG.map((beacon: any, idx: number) => {
                            const beaconWidth = 30;
                            const beaconHeight = 18;
                            const beaconScale = beacon.scale || 1;
                            
                            return (
                              <g key={`beacon-${idx}`} transform={`translate(${beacon.x}, ${beacon.y}) scale(${beaconScale}) translate(${-beacon.x}, ${-beacon.y})`}>
                                <rect
                                  x={beacon.x - beaconWidth / 2}
                                  y={beacon.y - beaconHeight / 2}
                                  width={beaconWidth}
                                  height={beaconHeight}
                                  fill={beacon.color}
                                  stroke="black"
                                  strokeWidth="0.5"
                                />
                              </g>
                            );
                          })}
                        </svg>
                      </>
                    )}
                  </div>
                </TransformComponent>
              </>
            )}
          </TransformWrapper>
        </div>
        <div
          ref={mapRef}
          style={{
            height: '100%',
            background: '#F7F7F7',
            display: !mapGeoreferenced ? 'none' : 'block'
          }}
        >
          {listMaps.length != 0 && mapGeoreferenced &&
            <AzureMapComponent
              map={selectedMap ?? null}
              zones={JSON.parse(localStorage.getItem('zonesChecked') || '[]')}
              devices={liveStatusMap ? localStorage.getItem('devicesLive') : localStorage.getItem('devicesChecked') ?? [...deviceChecked]}
              beacons={JSON.parse(localStorage.getItem('beaconsByMapId') || '[]')}
              groupingEnabled={groupingEnabled}
              deviceMac={deviceMacSelected}
              groupChanged={groupChanged}
              onlyBound={onlyBound}
              alarmInfo={JSON.parse(localStorage.getItem('alarmInfoSelected') || '[]')}
              alarmToggle={alarmToggle}
              geoStaticToggle={toggleGeoStatic}
            />
          }
        </div>
      </div>

      <div className="flex-right-child-element-maps" >
        {/* map list */}
        <h4 className="k-h4" style={{ color: '#018CA5' }}>{localizationService.toLanguageString('map.map_list', 'NOT_FOUND')}</h4>
        <DropDownList
          data={listMapNames}
          value={mapName.length > 0 ? mapName : undefined}
          defaultItem={localizationService.toLanguageString('map.select_map', 'NOT_FOUND')}
          onChange={async (e: any) => {
            setDeviceMacSelected('')
            localStorage.removeItem('deviceMapId')
            localStorage.removeItem('deviceGroupId');
            localStorage.removeItem('mapGeoreferenced');

            // @ts-ignore
            if (listMapNames.includes(e.value)) {
              localStorage.removeItem('alarmInfoSelected');
              localStorage.removeItem('beaconFromGrid');
              await changeMap(e, true)
            }
          }}
          style={{ width: "90%", color: '#018CA5', cursor: 'pointer' }}
          title={localizationService.toLanguageString('map.select_map', 'NOT_FOUND')}
        />
        <br />
        <br />

        {/*liveStatusMap*/}
        <div className="k-mb-2" >
          <Switch
            onChange={onToggleLive}
            disabled={(mapId != '' && (deviceMacFromSynoptic == null ? false : true) && deviceMapIdFromSynoptic == mapId)}
            checked={liveStatusMap}
          />
          <label className="k-ml-3">{localizationService.toLanguageString('map.live', 'NOT_FOUND')}</label>
        </div>

        {/*onlyBound*/}
        <div className="k-mb-2">
          <Switch
            onChange={onToggleOnlyBound}
            checked={onlyBound}
            disabled={((deviceMacFromSynoptic != null ? true : false) && deviceMapIdFromSynoptic == mapId)}
          />
          <label style={{ margin: '0 auto' }} className="k-ml-3">{localizationService.toLanguageString('synoptic_devices.only_binded', 'NOT_FOUND')}</label>
        </div>

        {/*toggleGeoStatic*/}
        {selectedMap?.georeferenced == 1 && selectedMap?.image != '' &&
          <div className="k-mb-2">
            <Switch
              onChange={onToggleGeoStatic}
              checked={toggleGeoStatic}
            />
            <label
              style={{ margin: '0 auto' }}
              className="k-ml-3"
            >
              {toggleGeoStatic ?
                localizationService.toLanguageString('map.geo', 'NOT_FOUND')
                :
                localizationService.toLanguageString('map.static', 'NOT_FOUND')
              }
            </label>
          </div>
        }

        {selectedMap?.georeferenced == 1 &&
          <div className="k-mb-2">
            <Switch
              onChange={onToggleGrouping}
              checked={groupingEnabled}
            />
            <label
              style={{ margin: '0 auto' }}
              className="k-ml-3"
            >
              Raggruppamento
            </label>
          </div>
        }

        <br />

        {/*filter device by synoptic*/}
        <div style={{ display: `${showDeviceMacBySynoptic ? 'block' : 'none'}` }} >
          <div
            className="trash-icon"
            style={{
              float: 'left',
              cursor: 'pointer',
              alignContent: 'center',
              marginRight: '10px'
            }}
            title={localizationService.toLanguageString('map.remove_filter', 'NOT FOUND')}
            onClick={() => {
              setShowDeviceMacBySynoptic(false);
              setDeviceMacSelected('');
              localStorage.removeItem('deviceMac');
              localStorage.removeItem('mapSelected');
              localStorage.removeItem('deviceMacFromSynoptic');
              localStorage.removeItem('deviceMapIdFromSynoptic');
              refreshGroupPositionsInMap(mapId, mapGeoreferenced);
            }}
          >
            <Avatar
              className="trash-icon"
            >
              <SvgIcon
                icon={trashIcon}
                style={{ verticalAlign: 'baseline', margin: '10px' }}
              />
            </Avatar>
          </div>
          <p
            style={{
              marginLeft: '10px',
              padding: '10px'
            }}
          >
            <label className="k-mb-2">Mac: {deviceMacFromSynoptic}</label>
          </p>
        </div>

        {/*filter beacon*/}
        <div style={{ display: `${showBeaconMac ? 'block' : 'none'}` }} >
          <div
            className="trash-icon"
            style={{
              float: 'left',
              cursor: 'pointer',
              alignContent: 'center',
              marginRight: '10px'
            }}
            title={localizationService.toLanguageString('map.remove_filter', 'NOT FOUND')}
            onClick={() => {
              setShowBeaconMac(false);
              changeCheckboxBeacon(true, beaconIdSelected);
              setBeaconIdSelected('');
              onChangeBeacon({});
              localStorage.removeItem('deviceMac');
              localStorage.removeItem('mapSelected');
              localStorage.removeItem('mapIdByBeacon');
              localStorage.removeItem('beaconFromGrid');
            }}
          >
            <Avatar
              className="trash-icon"
            >
              <SvgIcon
                icon={trashIcon}
                style={{ verticalAlign: 'baseline', margin: '10px' }}
              />
            </Avatar>
          </div>
          <p
            style={{
              marginLeft: '10px',
              padding: '10px'
            }}
          >
            <label className="k-mb-2">Mac: {beaconIdSelected}</label>
          </p>
        </div>

        <br />
        <br />

        {/* zone list */}
        <h4 className="k-h4" style={{ color: '#018CA5' }}>{localizationService.toLanguageString('map.zone_list', 'NOT_FOUND')}</h4>
        <div
          style={{
            maxHeight: '260px',
            overflowY: 'auto',
            paddingRight: '8px'
          }}
        >
          {zonesCheckboxByMap.map((zone: any, index: any) => {
            return (
              <>
                <Checkbox
                  key={index}
                  name={zone?.name ?? zone}
                  onChange={(e: any) => changeZone(e, zone?.id)}
                  label={zone.name ?? zone}
                  checked={zone.checked ?? false}
                >
                  <label className="checkbox-label-zone" style={{ background: zone?.color }} />
                </Checkbox>
              </>
            );
          })}
        </div>
        <br />
        <br />

        {/* group list */}
        <div>
          <p className="k-h4" style={{ color: '#018CA5' }}>
            {localizationService.toLanguageString('map.group_list', 'NOT_FOUND')}
          </p>
          {
            !mapGeoreferenced ?
              groupsCheckboxByMap?.map((group: any, index: any) => {
                return (
                  <>
                    <Checkbox
                      key={index}
                      name={group?.name ?? group}
                      value={group?.name ?? group}
                      onChange={(e: any) => changeGroup(e, group?.id, false)}
                      label={group.name ?? group}
                      checked={group.checked}
                      disabled={deviceMacFromSynoptic != null ? true : false}
                    >
                      <label className="checkbox-label-group" style={{ background: group?.color }} />
                    </Checkbox>
                  </>
                );
              })
              :
              groupsCheckboxByMap?.map((group: any, index: any) => {
                return (
                  <>
                    <Checkbox
                      key={index}
                      name={group?.name ?? group}
                      value={group?.name ?? group}
                      onChange={(e: any) => changeGroup(e, group?.id, true)}
                      label={group?.name ?? ''}
                      checked={group?.checked}
                    >
                      <label className="checkbox-label-group" style={{ background: group?.color }} />
                    </Checkbox>
                  </>
                );
              })
          }
        </div>
        <br />
        <br />

        {/* beacon list */}
        <div style={{ display: 'inline-table', width: '90%' }}>
          <h4 className="k-h4" style={{ color: '#018CA5' }}>{localizationService.toLanguageString('beacons.title', 'NOT_FOUND')}</h4>
          
          <MultiSelect
            data={listBeaconsOn}
            textField="id"
            dataItemKey="id"
            //value={JSON.parse(localStorage.getItem('beaconFromGrid') as string) != null ? JSON.parse(localStorage.getItem('beaconFromGrid') as string) : selectedBeacons}
            value={selectedBeacons}
            onChange={(e: any) => onChangeBeacon(e)}
            itemRender={beaconsRenderList}
            autoClose={false}
            filterable={true}
            onFilterChange={filterChange2}
            onFocus={filterChange2}
            tagRender={tagRender}
            disabled={showBeaconMac}
            placeholder={localizationService.toLanguageString('beacons.select_beacon', 'NOT_FOUND')}
          />
        </div>
        <br />
        <br />

        {/* alarm checkbox */}
        {alarmChecked &&
          <>
            <h4 className="k-h4" style={{ color: '#018CA5' }}>{localizationService.toLanguageString('map.alarm', 'NOT_FOUND')}</h4>
            <Checkbox
              name={alarmChecked?.name}
              value={alarmToggle ?? undefined}
              onChange={(e: any) => {
                changeAlarm(e)
                setAlarmInfo(alarmChecked)
              }}
              label={alarmChecked?.name}
            >
              <label className="checkbox-label-beacon" style={{ background: 'red' }} />
            </Checkbox>
          </>
        }
        <br />
        <br />
      </div>
    </div>
  )
}